import type { FC } from '../../lib/teact/teact';
import React, { useEffect, useState } from '../../lib/teact/teact';

import { checkSellerAuthStatus, SellerApiError } from '../../api/services/sellerAuth';
import {
  clearSellerSession,
  getPendingCredentials,
  getSellerDisplayName,
  setSellerSession,
} from '../../util/sellerSession';

import useLastCallback from '../../hooks/useLastCallback';

import './SellerAuth.scss';

const POLL_MS = 5000;

const SellerPending: FC = () => {
  const creds = getPendingCredentials();
  const displayName = getSellerDisplayName() || creds?.username || 'продавец';
  const [error, setError] = useState<string | null>(null);
  const [statusLine, setStatusLine] = useState('Проверяем статус…');
  const [lastCheckedAt, setLastCheckedAt] = useState<string | null>(null);
  const [isChecking, setIsChecking] = useState(false);

  const enterActive = useLastCallback((accessToken: string, refreshToken: string, name?: string) => {
    setStatusLine('Одобрено — открываем кабинет…');
    setSellerSession({ accessToken, refreshToken, name });
    window.location.reload();
  });

  const pollOnce = useLastCallback(async () => {
    if (!creds || isChecking) return;

    setIsChecking(true);
    try {
      const result = await checkSellerAuthStatus({
        username: creds.username,
        password: creds.password,
      });

      const now = new Date();
      const time = now.toLocaleTimeString('ru-RU', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      });
      setLastCheckedAt(time);
      setError(null);

      if (result.status === 'ACTIVE' && result.accessToken && result.refreshToken) {
        enterActive(
          result.accessToken,
          result.refreshToken,
          result.seller?.name || creds.username,
        );
        return;
      }

      if (result.status === 'BLOCKED') {
        setStatusLine('Аккаунт заблокирован.');
        setError('Обратитесь в поддержку, если это ошибка.');
        return;
      }

      setStatusLine('Ждём одобрения администратора.');
    } catch (err) {
      if (err instanceof SellerApiError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось проверить статус');
      }
      setStatusLine('Нет связи. Повторяем…');
    } finally {
      setIsChecking(false);
    }
  });

  useEffect(() => {
    if (!creds) return undefined;

    void pollOnce();
    const id = window.setInterval(() => {
      void pollOnce();
    }, POLL_MS);

    return () => window.clearInterval(id);
  // eslint-disable-next-line react-hooks-static-deps/exhaustive-deps
  }, []);

  const handleManualCheck = useLastCallback(() => {
    void pollOnce();
  });

  const handleBack = useLastCallback(() => {
    clearSellerSession();
    window.location.reload();
  });

  return (
    <div className="SellerGate custom-scroll">
      <div className="SellerGate__card SellerWait">
        <div className="SellerGate__brand">
          <div className="SellerGate__mark">MyZakaz</div>
        </div>

        <div className="SellerWait__pulse" aria-hidden>
          <span className="SellerWait__pulseRing" />
          <span className="SellerWait__pulseDot" />
        </div>

        <h1 className="SellerGate__title">Ожидание одобрения</h1>
        <p className="SellerGate__lede">
          Аккаунт на проверке. Страница обновляет статус сама —
          повторный вход не нужен.
        </p>

        <div className="SellerWait__ticket">
          <p className="SellerWait__ticketLabel">Заявка</p>
          <p className="SellerWait__ticketValue">@{displayName.replace(/^@/, '')}</p>
        </div>

        {error && (
          <div className="SellerGate__error" role="alert">
            <span className="SellerGate__errorIcon" aria-hidden>!</span>
            <span>{error}</span>
          </div>
        )}

        {!creds ? (
          <>
            <p className="SellerWait__status">
              Данные сессии на этом устройстве не найдены.
              Войдите ещё раз — если аккаунт всё ещё ждёт одобрения, вы вернётесь сюда.
            </p>
            <div className="SellerWait__actions">
              <button type="button" className="SellerGate__submit" onClick={handleBack}>
                К входу
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="SellerWait__status">{statusLine}</p>
            <p className="SellerWait__meta">
              {isChecking
                ? 'Проверяем…'
                : lastCheckedAt
                  ? `Последняя проверка ${lastCheckedAt}`
                  : 'Запускаем проверку…'}
              {' · '}
              каждые {POLL_MS / 1000} с
            </p>
            <div className="SellerWait__actions">
              <button
                type="button"
                className="SellerGate__submit"
                onClick={handleManualCheck}
                disabled={isChecking}
              >
                {isChecking ? 'Проверяем…' : 'Проверить сейчас'}
              </button>
              <button type="button" className="SellerWait__secondary" onClick={handleBack}>
                Другой аккаунт
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SellerPending;
